import {
  Bot,
  MessageSquare,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Copy,
  ExternalLink,
  History,
  Info,
  Loader2,
  Play,
  Plus,
  RefreshCw,
  Search,
  Send,
  Share2,
  Sparkles,
  User,
  Wrench,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import {
  fetchMarketplace,
  fetchSessionDetail,
  fetchSessionSnapshot,
  reserveSkillRequestUID,
  sendAgentMessage,
  shareURL,
  type MarketplaceData,
} from "./api";
import { currentUser, formatStatus, formatTaskStatus } from "./data";
import type {
  AgenticSkill,
  ChatState,
  SessionSnapshot,
  SkillChatMessage,
  SkillChatSession,
  SkillDepartment,
  SkillTaskMetadata,
  SkillTaskStatus,
  SkillUser,
} from "./types";

type Route =
  | { name: "suggested" }
  | { name: "skills" }
  | { name: "skill"; skillID: string }
  | { name: "skill-chat"; skillID: string }
  | { name: "morpheus" }
  | { name: "architect" }
  | { name: "profile"; userID?: string };

const emptyMarketplace: MarketplaceData = {
  skills: [],
  users: [],
  departments: [],
};

const emptySnapshot: SessionSnapshot = {
  sessions: [],
  taskStatuses: {},
  taskMetadata: {},
  isLoading: false,
  hasLoaded: false,
};

const sessionSnapshotCache = new Map<string, SessionSnapshot>();

function sessionCacheKey(state: ChatState, skillID?: string): string {
  return state === "skill_chat" ? `${state}:${skillID ?? "unknown"}` : state;
}

function readSessionCache(key: string): SessionSnapshot {
  const cached = sessionSnapshotCache.get(key);
  return cached ? { ...cached, isLoading: false, hasLoaded: true } : emptySnapshot;
}

function writeSessionCache(key: string, snapshot: SessionSnapshot): void {
  sessionSnapshotCache.set(key, { ...snapshot, isLoading: false, hasLoaded: true });
}

export function App() {
  const [route, setRoute] = useState<Route>(() => routeFromLocation());
  const [marketplace, setMarketplace] = useState<MarketplaceData>(emptyMarketplace);
  const [isLoadingMarketplace, setIsLoadingMarketplace] = useState(false);
  const [marketplaceError, setMarketplaceError] = useState<string>();

  useEffect(() => {
    const onPopState = () => setRoute(routeFromLocation());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    void loadMarketplace();
  }, []);

  async function loadMarketplace() {
    setIsLoadingMarketplace(true);
    setMarketplaceError(undefined);

    try {
      setMarketplace(await fetchMarketplace());
    } catch (error) {
      setMarketplaceError(error instanceof Error ? error.message : "Could not load skills.");
    } finally {
      setIsLoadingMarketplace(false);
    }
  }

  function navigate(nextRoute: Route) {
    window.history.pushState({}, "", pathForRoute(nextRoute));
    setRoute(nextRoute);
  }

  return (
    <>
      <title>The Construct</title>
      <AppShell route={route} navigate={navigate}>
        {route.name === "suggested" && (
          <SuggestedPage
            marketplace={marketplace}
            isLoading={isLoadingMarketplace}
            error={marketplaceError}
          />
        )}
        {route.name === "skills" && (
          <SkillsPage
            marketplace={marketplace}
            isLoading={isLoadingMarketplace}
            error={marketplaceError}
            onRefresh={loadMarketplace}
          />
        )}
        {route.name === "skill" && (
          <SkillDetailPage
            skill={marketplace.skills.find((skill) => skill.id === route.skillID)}
            users={marketplace.users}
            navigate={navigate}
          />
        )}
        {route.name === "skill-chat" && (
          <ChatPage
            state="skill_chat"
            title={marketplace.skills.find((skill) => skill.id === route.skillID)?.name ?? "Skill chat"}
            skill={marketplace.skills.find((skill) => skill.id === route.skillID)}
          />
        )}
        {route.name === "morpheus" && <ChatPage state="brainstorm" title="Morpheus" />}
        {route.name === "architect" && <ChatPage state="create_skill" title="Architect" skills={marketplace.skills} />}
        {route.name === "profile" && (
          <ProfilePage user={resolveUser(marketplace.users, route.userID)} skills={marketplace.skills} />
        )}
      </AppShell>
    </>
  );
}

function AppShell({ route, navigate, children }: { route: Route; navigate: (route: Route) => void; children: ReactNode }) {
  const navItems = [
    { label: "Explore", route: { name: "suggested" } as Route, icon: Sparkles, active: route.name === "suggested" },
    { label: "Morpheus", route: { name: "morpheus" } as Route, icon: Bot, active: route.name === "morpheus" },
    { label: "Architect", route: { name: "architect" } as Route, icon: Wrench, active: route.name === "architect" },
    { label: "Skills", route: { name: "skills" } as Route, icon: Clipboard, active: route.name === "skills" },
    { label: "Profile", route: { name: "profile" } as Route, icon: User, active: route.name === "profile" },
  ];

  return (
    <div className="app-shell">
      <aside className="sidebar" aria-label="App navigation">
        <button className="brand-button sidebar-brand" type="button" onClick={() => navigate({ name: "suggested" })}>
          <span>The Construct</span>
        </button>
        <nav className="sidebar-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <button className={item.active ? "nav-item active" : "nav-item"} type="button" key={item.label} onClick={() => navigate(item.route)}>
                <Icon size={18} aria-hidden="true" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="app-frame">
        <main className="app-main page-width">{children}</main>
      </div>
    </div>
  );
}

function SkillsPage({
  marketplace,
  isLoading,
  error,
  onRefresh,
}: {
  marketplace: MarketplaceData;
  isLoading: boolean;
  error?: string;
  onRefresh: () => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [selectedSkill, setSelectedSkill] = useState<AgenticSkill | null>(null);

  const directoryDepartments = useMemo(() => filterDepartments(marketplace.departments, query), [marketplace.departments, query]);
  const directoryUsers = useMemo(() => filterUsers(marketplace.users, query), [marketplace.users, query]);

  return (
    <section className="stack">
      <div className="page-panel">
        <div className="tab-heading">
          <h2 className="tab-title">Skills</h2>
          <div className="toolbar compact">
            <label className="search-field">
              <Search size={16} aria-hidden="true" />
              <span className="sr-only">Search skills</span>
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search skills" />
            </label>

            <button className="icon-button" type="button" onClick={() => void onRefresh()} disabled={isLoading} aria-label="Refresh skills">
              <RefreshCw size={17} aria-hidden="true" />
            </button>
          </div>
        </div>

        {isLoading ? (
          <StatePanel icon={<Loader2 className="spin" size={20} />} title="Loading skills" text="The marketplace is being refreshed." />
        ) : error ? (
          <StatePanel title="Skills could not load" text={error} />
        ) : (
          <DirectoryView departments={directoryDepartments} users={directoryUsers} onOpen={setSelectedSkill} />
        )}
      </div>
      {selectedSkill && <SkillDetailModal skill={selectedSkill} onClose={() => setSelectedSkill(null)} />}
    </section>
  );
}

function filterDepartments(departments: SkillDepartment[], query: string): SkillDepartment[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return departments;

  return departments
    .map((department) => {
      const departmentMatches = department.name.toLowerCase().includes(normalizedQuery);
      const plugins = department.plugins
        .map((plugin) => {
          const pluginMatches = [plugin.title, plugin.description].join(" ").toLowerCase().includes(normalizedQuery);
          const skills = pluginMatches || departmentMatches ? plugin.skills : plugin.skills.filter((skill) => skillMatchesQuery(skill, normalizedQuery));
          return { ...plugin, skills };
        })
        .filter((plugin) => plugin.skills.length > 0);
      return { ...department, plugins };
    })
    .filter((department) => department.plugins.length > 0);
}

function filterUsers(users: SkillUser[], query: string): SkillUser[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return users;

  return users
    .map((user) => {
      const userMatches = [user.id, user.name, user.email, user.department, user.team ?? ""].join(" ").toLowerCase().includes(normalizedQuery);
      const skills = userMatches ? user.skills : user.skills.filter((skill) => skillMatchesQuery(skill, normalizedQuery));
      return { ...user, skills };
    })
    .filter((user) => user.skills.length > 0);
}

function skillMatchesQuery(skill: AgenticSkill, normalizedQuery: string): boolean {
  return [skill.name, skill.summary, skill.creatorName ?? "", skill.lastUpdated, formatStatus(skill.status)].join(" ").toLowerCase().includes(normalizedQuery);
}

function SuggestedPage({
  marketplace,
  isLoading,
  error,
}: {
  marketplace: MarketplaceData;
  isLoading: boolean;
  error?: string;
}) {
  const [selectedSkill, setSelectedSkill] = useState<AgenticSkill | null>(null);
  const showcasedSkills = useMemo(() => {
    const approvedSkills = marketplace.skills.filter((skill) => skill.status === "approved");
    const relevantSkills = approvedSkills.filter((skill) => skill.isRelevant);
    const orderedSkills = [...relevantSkills, ...approvedSkills, ...marketplace.skills];
    return orderedSkills.filter((skill, index, skills) => skills.findIndex((candidate) => candidate.id === skill.id) === index).slice(0, 5);
  }, [marketplace.skills]);

  return (
    <section className="stack">
      <div className="page-panel">
        <h2 className="tab-title">What's new</h2>
        {isLoading ? (
          <StatePanel icon={<Loader2 className="spin" size={20} />} title="Loading skills" text="The marketplace is being refreshed." />
        ) : error ? (
          <StatePanel title="Skills could not load" text={error} />
        ) : (
          <SuggestedShowcase skills={showcasedSkills} onOpen={setSelectedSkill} />
        )}
      </div>
      {selectedSkill && <SkillDetailModal skill={selectedSkill} onClose={() => setSelectedSkill(null)} />}
    </section>
  );
}

function SuggestedShowcase({ skills, onOpen }: { skills: AgenticSkill[]; onOpen: (skill: AgenticSkill) => void }) {
  if (skills.length === 0) {
    return <StatePanel title="No suggested skills" text="Approved skills will appear here when they are available." />;
  }

  return (
    <div className="showcase-grid">
      {skills.map((skill, index) => (
        <button className="showcase-card" type="button" key={skill.id} onClick={() => onOpen(skill)}>
          <span className="showcase-index mono">{String(index + 1).padStart(2, "0")}</span>
          <span className="showcase-content">
            <span className="showcase-kicker">
              {skill.creatorName ?? "Unknown"}
              <StatusBadge status={skill.status} />
            </span>
            <strong>{skill.name}</strong>
            <span>{skill.summary}</span>
          </span>
          <span className="showcase-action">
            Explore
            <ChevronRight size={16} aria-hidden="true" />
          </span>
        </button>
      ))}
    </div>
  );
}

function SkillRows({ skills, onOpen }: { skills: AgenticSkill[]; onOpen: (skill: AgenticSkill) => void }) {
  return (
    <div className="row-list">
      {skills.map((skill) => (
        <button type="button" className="skill-row" key={skill.id} onClick={() => onOpen(skill)}>
          <span>
            <strong>{skill.name}</strong>
          </span>
          <StatusBadge status={skill.status} />
        </button>
      ))}
    </div>
  );
}

function ProfileSkillRows({ skills, onOpen }: { skills: AgenticSkill[]; onOpen: (skill: AgenticSkill) => void }) {
  return (
    <div className="profile-skill-list">
      {skills.map((skill) => (
        <button type="button" className="profile-skill-row" key={skill.id} onClick={() => onOpen(skill)}>
          <span className="profile-skill-main">
            <strong>{skill.name}</strong>
            <span>{skill.summary}</span>
          </span>
          <StatusBadge status={skill.status} />
        </button>
      ))}
    </div>
  );
}

function DirectoryView({ departments, users, onOpen }: { departments: SkillDepartment[]; users: SkillUser[]; onOpen: (skill: AgenticSkill) => void }) {
  const [expandedTiles, setExpandedTiles] = useState<Set<string>>(new Set());
  const departmentTiles = departments.map((department, index) => ({
    id: `department-${index}-${department.id}`,
    title: department.name,
    subtitle: `${department.plugins.length} ${department.plugins.length === 1 ? "group" : "groups"}`,
    skills: department.plugins.flatMap((plugin) => plugin.skills),
  }));
  const userTiles = users.map((user, index) => ({
    id: `user-${index}-${user.id}`,
    title: user.id,
    subtitle: user.name,
    skills: user.skills,
  }));
  const tiles = [...departmentTiles, ...userTiles].filter((tile) => tile.skills.length > 0);

  return (
    <div className="directory">
      {tiles.length === 0 ? (
        <StatePanel title="Directory is empty" text="Skills will appear here after the marketplace is indexed." />
      ) : (
        tiles.map((tile) => (
          <DirectoryTile
            key={tile.id}
            title={tile.title}
            subtitle={tile.subtitle}
            skills={tile.skills}
            isExpanded={expandedTiles.has(tile.id)}
            onToggle={() => setExpandedTiles((current) => toggleSet(current, tile.id))}
            onOpen={onOpen}
          />
        ))
      )}
    </div>
  );
}

function DirectoryTile({
  title,
  subtitle,
  skills,
  isExpanded,
  onToggle,
  onOpen,
}: {
  title: string;
  subtitle?: string;
  skills: AgenticSkill[];
  isExpanded: boolean;
  onToggle: () => void;
  onOpen: (skill: AgenticSkill) => void;
}) {
  return (
    <section className="directory-tile">
      <button className="directory-tile-button" type="button" onClick={onToggle} aria-expanded={isExpanded}>
        <span>
          <strong>{title}</strong>
          {subtitle && <small>{subtitle}</small>}
        </span>
        <span className="directory-tile-meta">
          {skills.length} {skills.length === 1 ? "skill" : "skills"}
          {isExpanded ? <ChevronDown size={16} aria-hidden="true" /> : <ChevronRight size={16} aria-hidden="true" />}
        </span>
      </button>
      {isExpanded && (
        <div className="directory-tile-content">
          <SkillRows skills={skills} onOpen={onOpen} />
        </div>
      )}
    </section>
  );
}

function SkillDetailModal({ skill, onClose }: { skill: AgenticSkill; onClose: () => void }) {
  const [isInstallOpen, setIsInstallOpen] = useState(false);
  const [isCopiedVisible, setIsCopiedVisible] = useState(false);
  const installCommand = `npx --yes skills install ${skill.id}`;

  async function copyShareLink() {
    await navigator.clipboard?.writeText(shareURL(skill.id)).catch(() => undefined);
    setIsCopiedVisible(true);
    window.setTimeout(() => setIsCopiedVisible(false), 1600);
  }

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section className="skill-modal" role="dialog" aria-modal="true" aria-labelledby="skill-modal-title" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header skill-modal-header">
          <h2 id="skill-modal-title">{skill.name}</h2>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="skill-modal-scroll">
          <div className="skill-modal-stack">
            <SkillField label="Description">
              <p>{skill.summary}</p>
            </SkillField>
            <SkillField label="Creator">
              <p>{skill.creatorName ?? "Unknown"}</p>
            </SkillField>
            <SkillField label="Last updated">
              <p className="mono">{skill.lastUpdated}</p>
            </SkillField>

            <section className="markdown-box">
              <h3>Pull request content</h3>
              <MarkdownContent source={skill.details} />
            </section>
          </div>
        </div>

        <div className="skill-modal-actions">
          <StatusBadge status={skill.status} />
          <div className="skill-modal-action-buttons">
            <button className="button secondary" type="button" onClick={() => void copyShareLink()}>
              <Share2 size={16} aria-hidden="true" />
              Share
            </button>
            <button className="button secondary" type="button" disabled={skill.status !== "approved"} onClick={() => setIsInstallOpen(true)}>
              <Clipboard size={16} aria-hidden="true" />
              Install
            </button>
          </div>
        </div>

        {isInstallOpen && <InstallCommandModal skill={skill} installCommand={installCommand} onClose={() => setIsInstallOpen(false)} />}
        {isCopiedVisible && (
          <div className="toast" role="status" aria-live="polite">
            Copied
          </div>
        )}
      </section>
    </div>
  );
}

function SkillField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="skill-field">
      <span>{label}</span>
      {children}
    </section>
  );
}

function MarkdownContent({ source }: { source: string }) {
  const blocks = source.split(/\n{2,}/).filter((block) => block.trim().length > 0);

  return (
    <div className="markdown-content">
      {blocks.map((rawBlock, index) => {
        const block = rawBlock.trim();
        const heading = block.match(/^(#{1,3})\s+(.+)$/);
        const listItems = block.split("\n").map((line) => line.match(/^[-*]\s+(.+)$/)?.[1]);
        const orderedItems = block.split("\n").map((line) => line.match(/^\d+\.\s+(.+)$/)?.[1]);

        if (block.startsWith("```") && block.endsWith("```")) {
          return (
            <pre key={index}>
              <code>{block.replace(/^```\w*\n?/, "").replace(/```$/, "").trim()}</code>
            </pre>
          );
        }

        if (heading) {
          const HeadingTag = heading[1].length === 1 ? "h2" : "h3";
          return <HeadingTag key={index}>{heading[2]}</HeadingTag>;
        }

        if (listItems.every(Boolean)) {
          return (
            <ul key={index}>
              {listItems.map((item) => (
                <li key={item}>{renderInlineMarkdown(item ?? "")}</li>
              ))}
            </ul>
          );
        }

        if (orderedItems.every(Boolean)) {
          return (
            <ol key={index}>
              {orderedItems.map((item) => (
                <li key={item}>{renderInlineMarkdown(item ?? "")}</li>
              ))}
            </ol>
          );
        }

        return <p key={index}>{renderInlineMarkdown(block)}</p>;
      })}
    </div>
  );
}

function renderInlineMarkdown(text: string): ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g).filter(Boolean);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`")) {
      return <code key={index}>{part.slice(1, -1)}</code>;
    }
    if (part.startsWith("*") && part.endsWith("*")) {
      return <em key={index}>{part.slice(1, -1)}</em>;
    }
    return <span key={index}>{part}</span>;
  });
}

function hasMarkdownSyntax(text: string): boolean {
  return /(^|\n)#{1,3}\s|(^|\n)([-*]|\d+\.)\s|```|`[^`]+`|\*\*[^*]+\*\*|\*[^*]+\*/.test(text);
}

function InstallCommandModal({ skill, installCommand, onClose }: { skill: AgenticSkill; installCommand: string; onClose: () => void }) {
  async function copyInstallCommand() {
    await navigator.clipboard?.writeText(installCommand);
  }

  return (
    <div className="modal-backdrop nested" role="presentation" onClick={onClose}>
      <section className="install-modal" role="dialog" aria-modal="true" aria-labelledby="install-title" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow">Install skill</p>
            <h2 id="install-title">{skill.name}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <p className="supporting compact">Run this command in your terminal to install the skill with npx skills.</p>
        <div className="command-row">
          <code>{installCommand}</code>
          <button className="icon-button" type="button" onClick={() => void copyInstallCommand()} aria-label="Copy install command">
            <Copy size={16} aria-hidden="true" />
          </button>
        </div>
      </section>
    </div>
  );
}

function SkillDetailPage({ skill, users, navigate }: { skill?: AgenticSkill; users: SkillUser[]; navigate: (route: Route) => void }) {
  const creator = users.find((user) => user.id === skill?.creatorID);
  const [isInstallOpen, setIsInstallOpen] = useState(false);

  if (!skill) {
    return (
      <section className="surface-panel page-panel">
        <BackButton label="Skills" onClick={() => navigate({ name: "skills" })} />
        <StatePanel title="Skill not found" text="This skill is not available in the marketplace." />
      </section>
    );
  }

  const resolvedSkill = skill;
  const installCommand = `npx --yes skills install ${skill.id}`;

  async function copyShareLink() {
    await navigator.clipboard?.writeText(shareURL(resolvedSkill.id));
  }

  return (
    <section className="detail-grid">
      <div className="surface-panel page-panel">
        <BackButton label="Skills" onClick={() => navigate({ name: "skills" })} />
        <div className="detail-heading">
          <div>
            <p className="eyebrow">Skill</p>
            <h2>{skill.name}</h2>
          </div>
          <StatusBadge status={skill.status} />
        </div>

        <div className="metadata-grid">
          <Metadata label="Description">{skill.summary}</Metadata>
          <Metadata label="Creator">
            {creator ? (
              <button className="link-button" type="button" onClick={() => navigate({ name: "profile", userID: creator.id })}>
                {creator.name}
              </button>
            ) : (
              skill.creatorName ?? "Unknown"
            )}
          </Metadata>
          <Metadata label="Last updated">
            <span className="mono">{skill.lastUpdated}</span>
          </Metadata>
        </div>

        <div className="details-copy">{skill.details}</div>
      </div>

      <aside className="surface-panel action-panel">
        <h3>Actions</h3>
        <button className="button secondary full" type="button" onClick={() => void copyShareLink()}>
          <Share2 size={16} aria-hidden="true" />
          Share
        </button>
        <button
          className="button secondary full"
          type="button"
          disabled={skill.status !== "approved"}
          onClick={() => setIsInstallOpen(true)}
        >
          <Clipboard size={16} aria-hidden="true" />
          Install
        </button>
      </aside>

      {isInstallOpen && <InstallCommandModal skill={skill} installCommand={installCommand} onClose={() => setIsInstallOpen(false)} />}
    </section>
  );
}

function ChatPage({ state, title, skill, skills = [] }: { state: ChatState; title: string; skill?: AgenticSkill; skills?: AgenticSkill[] }) {
  const cacheKey = sessionCacheKey(state, skill?.id);
  const cachedSnapshot = readSessionCache(cacheKey);
  const [snapshot, setSnapshot] = useState<SessionSnapshot>(cachedSnapshot);
  const [selectedSessionID, setSelectedSessionID] = useState(cachedSnapshot.sessions[0]?.id ?? "");
  const [draft, setDraft] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string>();
  const [reservedUID, setReservedUID] = useState<string>();
  const [isArchitectSessionOpen, setIsArchitectSessionOpen] = useState(false);
  const [selectedSkillDetail, setSelectedSkillDetail] = useState<AgenticSkill | null>(null);
  const [architectQuery, setArchitectQuery] = useState("");
  const transcriptRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const cached = readSessionCache(cacheKey);
    setSnapshot(cached);
    setSelectedSessionID(cached.sessions[0]?.id ?? "");
    void loadSessions(cached.sessions[0]?.id);
    if (state === "create_skill") {
      void reserveSkillRequestUID().then(setReservedUID).catch(() => undefined);
    }
  }, [cacheKey]);

  const selectedSession = snapshot.sessions.find((session) => session.id === selectedSessionID) ?? snapshot.sessions[0];
  const selectedStatus = selectedSession ? snapshot.taskStatuses[selectedSession.id] : undefined;
  const selectedMetadata = selectedSession ? snapshot.taskMetadata[selectedSession.id] : undefined;
  const filteredArchitectSessions = useMemo(() => {
    const normalizedQuery = architectQuery.trim().toLowerCase();
    if (!normalizedQuery) return snapshot.sessions;
    return snapshot.sessions.filter((session) => {
      const metadata = snapshot.taskMetadata[session.id];
      const status = snapshot.taskStatuses[session.id] ?? { kind: session.id.startsWith("sesn_") ? "running" : "waiting" };
      return [
        metadata?.name,
        metadata?.description,
        session.title,
        session.messages[0]?.text,
        formatSessionUpdated(session.updatedAt),
        formatTaskStatus(status),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);
    });
  }, [architectQuery, snapshot.sessions, snapshot.taskMetadata, snapshot.taskStatuses]);
  const hasRealSession = Boolean(selectedSession && selectedSession.id.startsWith("sesn_"));
  const chatMainClassName =
    state === "brainstorm"
      ? "surface-panel chat-main no-header"
      : state === "create_skill"
        ? "surface-panel chat-main architect-main"
        : "surface-panel chat-main";

  useEffect(() => {
    const transcript = transcriptRef.current;
    if (!transcript) return;
    requestAnimationFrame(() => {
      transcript.scrollTop = transcript.scrollHeight;
    });
  }, [selectedSession?.id, selectedSession?.messages.length, snapshot.isLoading, isSending, isArchitectSessionOpen]);

  async function loadSessions(preferredSessionID?: string) {
    setSnapshot((current) => ({ ...current, isLoading: true }));
    setError(undefined);

    try {
      const next = await fetchSessionSnapshot(state);
      const preferredFromList = preferredSessionID ? next.sessions.find((session) => session.id === preferredSessionID) : undefined;
      const nextSelectedSessionID = preferredFromList?.id || next.sessions[0]?.id || "";
      const selectedFromList = next.sessions.find((session) => session.id === nextSelectedSessionID);
      const hydrated =
        nextSelectedSessionID.startsWith("sesn_") && selectedFromList?.messages.length === 0
          ? mergeSnapshot(next, await fetchSessionDetail(state, nextSelectedSessionID))
          : next;
      writeSessionCache(cacheKey, hydrated);
      setSnapshot(hydrated);
      setSelectedSessionID(nextSelectedSessionID);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "Could not load sessions.");
      if (snapshot.sessions.length === 0) {
        const local = newLocalSession(state);
        const fallback = { ...emptySnapshot, sessions: [local], hasLoaded: true };
        writeSessionCache(cacheKey, fallback);
        setSnapshot(fallback);
        setSelectedSessionID(local.id);
      }
    }
  }

  async function refreshSelectedSession(sessionID: string) {
    if (!sessionID.startsWith("sesn_")) return;
    const detail = await fetchSessionDetail(state, sessionID);
    setSnapshot((current) => {
      const next = mergeSnapshot(current, detail);
      writeSessionCache(cacheKey, next);
      return next;
    });
  }

  function startNewSession() {
    const local = newLocalSession(state);
    setSnapshot((current) => {
      const next = { ...current, sessions: [local, ...current.sessions], hasLoaded: true };
      writeSessionCache(cacheKey, next);
      return next;
    });
    setSelectedSessionID(local.id);
    if (state === "create_skill") {
      setDraft("");
      setIsArchitectSessionOpen(true);
    }
  }

  async function sendMessage() {
    const message = draft.trim();
    if (!message || isSending) return;

    const activeSession = selectedSession ?? newLocalSession(state);
    const userMessage: SkillChatMessage = { id: crypto.randomUUID(), sender: "user", text: message };
    const optimisticSession: SkillChatSession = {
      ...activeSession,
      messages: [...activeSession.messages, userMessage],
      updatedAt: new Date().toISOString(),
    };

    setDraft("");
    setIsSending(true);
    setError(undefined);
    setSnapshot((current) => {
      const next = upsertSession(current, optimisticSession, state === "create_skill" ? { kind: activeSession.id.startsWith("sesn_") ? "running" : "waiting" } : undefined);
      writeSessionCache(cacheKey, next);
      return next;
    });

    try {
      const isNewCreateSkillRequest = state === "create_skill" && !activeSession.id.startsWith("sesn_");
      const response = await sendAgentMessage({
        state,
        message,
        sessionId: activeSession.id.startsWith("sesn_") ? activeSession.id : undefined,
        skillID: skill?.id,
        skillName: skill?.name,
        uid: isNewCreateSkillRequest ? reservedUID : undefined,
      });
      const realID = response.sessionId || activeSession.id;
      const assistantMessage = response.message ? [{ id: crypto.randomUUID(), sender: "assistant" as const, text: response.message }] : [];
      const updatedSession = {
        ...optimisticSession,
        id: realID,
        title: state === "create_skill" ? response.uid || optimisticSession.title : optimisticSession.title,
        messages: [...optimisticSession.messages, ...assistantMessage],
      };

      setSnapshot((current) => {
        const next = upsertSession(current, updatedSession, state === "create_skill" ? { kind: "running" } : undefined, activeSession.id);
        writeSessionCache(cacheKey, next);
        return next;
      });
      setSelectedSessionID(realID);
      if (state === "create_skill") {
        void reserveSkillRequestUID().then(setReservedUID).catch(() => undefined);
      }
      await refreshSelectedSession(realID).catch(() => undefined);
    } catch (sendError) {
      setError(sendError instanceof Error ? sendError.message : "Could not start the request.");
    } finally {
      setIsSending(false);
    }
  }

  function openArchitectSession(session: SkillChatSession) {
    setSelectedSessionID(session.id);
    setDraft("");
    setIsArchitectSessionOpen(true);
    if (session.id.startsWith("sesn_")) void refreshSelectedSession(session.id);
  }

  if (state === "create_skill") {
    return (
      <section className="architect-list-view">
        <div className="session-header">
          <h2 className="tab-title">Architect</h2>
          <div className="toolbar compact">
            <label className="search-field">
              <Search size={16} aria-hidden="true" />
              <span className="sr-only">Search sessions</span>
              <input value={architectQuery} onChange={(event) => setArchitectQuery(event.target.value)} placeholder="Search sessions" />
            </label>
            <div className="icon-group">
              <button className="icon-button" type="button" onClick={startNewSession} aria-label="New session">
                <Plus size={16} aria-hidden="true" />
              </button>
              <button className="icon-button" type="button" onClick={() => void loadSessions(selectedSessionID)} aria-label="Refresh sessions">
                <RefreshCw size={16} aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>

        <div className="architect-session-table">
          <div className="architect-session-header" aria-hidden="true">
            <span>Name</span>
            <span>Last updated</span>
            <span>Status</span>
            <span>Actions</span>
          </div>
          <div className="architect-session-list">
            {snapshot.isLoading && !snapshot.hasLoaded ? (
              <StatePanel icon={<Loader2 className="spin" size={20} />} title="Loading sessions" text="Conversation history is being refreshed." />
            ) : snapshot.sessions.length === 0 ? (
              <StatePanel icon={<History size={18} />} title="No sessions" text="Start a new skill request." />
            ) : filteredArchitectSessions.length === 0 ? (
              <StatePanel title="No sessions found" text="No Architect sessions match this search." />
            ) : (
              filteredArchitectSessions.map((session) => (
                <ArchitectSessionTile
                  key={session.id}
                  session={session}
                  metadata={snapshot.taskMetadata[session.id]}
                  status={snapshot.taskStatuses[session.id]}
                  skill={resolveSessionSkill(snapshot.taskMetadata[session.id], skills)}
                  onOpenChat={() => openArchitectSession(session)}
                  onOpenSkill={setSelectedSkillDetail}
                />
              ))
            )}
          </div>
        </div>

        {error && <div className="alert">{error}</div>}
        {isArchitectSessionOpen && selectedSession && (
          <ArchitectSessionModal
            session={selectedSession}
            metadata={selectedMetadata}
            status={selectedStatus}
            draft={draft}
            isSending={isSending}
            transcriptRef={transcriptRef}
            onDraftChange={setDraft}
            onClose={() => setIsArchitectSessionOpen(false)}
            onSubmit={sendMessage}
          />
        )}
        {selectedSkillDetail && <SkillDetailModal skill={selectedSkillDetail} onClose={() => setSelectedSkillDetail(null)} />}
      </section>
    );
  }

  return (
    <section className="chat-layout">
      <div className={chatMainClassName}>
        {state === "brainstorm" && <h2 className="tab-title">Morpheus</h2>}
        {state === "skill_chat" && (
          <div className="panel-header">
            <div>
              <p className="eyebrow">Skill chat</p>
              <h2>{title}</h2>
            </div>
            {selectedStatus && <TaskBadge status={selectedStatus} />}
          </div>
        )}

        <div className="transcript" aria-live="polite" ref={transcriptRef}>
          {snapshot.isLoading && !snapshot.hasLoaded ? (
            <StatePanel icon={<Loader2 className="spin" size={20} />} title="Loading sessions" text="Conversation history is being refreshed." />
          ) : selectedSession && selectedSession.messages.length > 0 ? (
            <>
              {selectedSession.messages.map((message) => (
                <ChatBubble key={message.id} message={message} />
              ))}
              {isSending && <ChatBubble message={{ id: "thinking", sender: "assistant", text: "Thinking..." }} />}
            </>
          ) : (
            <EmptyChat state={state} />
          )}
        </div>

        {error && <div className="alert">{error}</div>}

        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            void sendMessage();
          }}
        >
          <label className="sr-only" htmlFor="chat-draft">
            Message
          </label>
          <input id="chat-draft" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={placeholderForState(state)} disabled={isSending} />
          <button className="icon-button primary-icon" type="submit" disabled={!draft.trim() || isSending} aria-label="Send">
            <Send size={17} aria-hidden="true" />
          </button>
        </form>
      </div>

      <aside className="surface-panel session-panel">
        <div className="session-header">
          <h3>Sessions</h3>
          <div className="icon-group">
            <button className="icon-button" type="button" onClick={startNewSession} aria-label="New session">
              <Plus size={16} aria-hidden="true" />
            </button>
            <button className="icon-button" type="button" onClick={() => void loadSessions(selectedSessionID)} aria-label="Refresh sessions">
              <RefreshCw size={16} aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="session-list">
          {snapshot.sessions.length === 0 ? (
            <StatePanel icon={<History size={18} />} title="No sessions" text="Start a new conversation to create session history." />
          ) : (
            snapshot.sessions.map((session) => (
              <button
                className={session.id === selectedSessionID ? "session-row active" : "session-row"}
                type="button"
                key={session.id}
                onClick={() => {
                  setSelectedSessionID(session.id);
                  if (session.id.startsWith("sesn_")) void refreshSelectedSession(session.id);
                }}
              >
                <span>{session.title}</span>
              </button>
            ))
          )}
        </div>

      </aside>
    </section>
  );
}

function ArchitectSessionTile({
  session,
  metadata,
  status,
  skill,
  onOpenChat,
  onOpenSkill,
}: {
  session: SkillChatSession;
  metadata?: SkillTaskMetadata;
  status?: SkillTaskStatus;
  skill?: AgenticSkill;
  onOpenChat: () => void;
  onOpenSkill: (skill: AgenticSkill) => void;
}) {
  const title = metadata?.name || session.title;
  const description = metadata?.description || session.messages[0]?.text || "No description available.";
  const canOpenSkill = Boolean(skill && status && status.kind !== "running");

  return (
    <div className="architect-session-tile">
      <span className="tile-primary">
        <span className="tile-title">{title}</span>
        <span className="tile-description">{description}</span>
      </span>
      <span className="mono tile-date">{formatSessionUpdated(session.updatedAt)}</span>
      <span className="tile-status">{status ? <TaskBadge status={status} compact /> : <TaskBadge status={{ kind: session.id.startsWith("sesn_") ? "running" : "waiting" }} compact />}</span>
      <span className="tile-actions">
        <button
          type="button"
          className="icon-button tile-action-button"
          disabled={!canOpenSkill || !skill}
          aria-label={skill ? `Open skill details for ${skill.name}` : "Skill details unavailable"}
          onClick={() => {
            if (skill) onOpenSkill(skill);
          }}
        >
          <Info size={16} aria-hidden="true" />
        </button>
        <button type="button" className="icon-button tile-action-button" aria-label={`Open chat history for ${title}`} onClick={onOpenChat}>
          <MessageSquare size={16} aria-hidden="true" />
        </button>
      </span>
    </div>
  );
}

function ArchitectSessionModal({
  session,
  metadata,
  status,
  draft,
  isSending,
  transcriptRef,
  onDraftChange,
  onClose,
  onSubmit,
}: {
  session: SkillChatSession;
  metadata?: { name?: string; description?: string };
  status?: SkillTaskStatus;
  draft: string;
  isSending: boolean;
  transcriptRef: RefObject<HTMLDivElement | null>;
  onDraftChange: (value: string) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const title = metadata?.name || session.title;

  return (
    <div className="modal-backdrop" role="presentation" onClick={onClose}>
      <section className="architect-chat-modal" role="dialog" aria-modal="true" aria-labelledby="architect-session-title" onClick={(event) => event.stopPropagation()}>
        <div className="modal-header skill-modal-header">
          <div className="architect-modal-title-row">
            <h2 id="architect-session-title">{title}</h2>
            {status && <TaskBadge status={status} />}
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close">
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        <div className="transcript architect-modal-transcript" aria-live="polite" ref={transcriptRef}>
          {session.messages.length > 0 ? (
            <>
              {session.messages.map((message) => (
                <ChatBubble key={message.id} message={message} />
              ))}
              {isSending && <ChatBubble message={{ id: "thinking", sender: "assistant", text: "Thinking..." }} />}
            </>
          ) : (
            <EmptyChat state="create_skill" />
          )}
        </div>

        <form
          className="composer"
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <label className="sr-only" htmlFor="architect-session-draft">
            Message
          </label>
          <input id="architect-session-draft" value={draft} onChange={(event) => onDraftChange(event.target.value)} placeholder={placeholderForState("create_skill")} disabled={isSending} />
          <button className="icon-button primary-icon" type="submit" disabled={!draft.trim() || isSending} aria-label="Send">
            <Send size={17} aria-hidden="true" />
          </button>
        </form>
      </section>
    </div>
  );
}

function ArchitectOverview({
  session,
  metadata,
  status,
}: {
  session?: SkillChatSession;
  metadata?: { name?: string; description?: string };
  status?: SkillTaskStatus;
}) {
  const title = metadata?.name || session?.title || "New Skill Request";
  const description = metadata?.description || "Describe the skill or pull request you want Architect to create.";
  const statusKind = status?.kind ?? "waiting";

  return (
    <div className="architect-overview">
      <div className="architect-summary">
        <h2>{title}</h2>
        <p>{description}</p>
        {status?.pullRequestURL && (
          <a href={status.pullRequestURL} target="_blank" rel="noreferrer">
            More info
          </a>
        )}
      </div>
      <div className="architect-status-track" aria-label={`Request status: ${formatTaskStatus(status ?? { kind: "waiting" })}`}>
        <ArchitectStep tone="running" label="Running" isActive={statusKind === "waiting" || statusKind === "running"} isComplete={statusKind === "in_review" || statusKind === "approved"} icon={<Play size={22} />} />
        <ArchitectStep tone="review" label="In Review" isActive={statusKind === "in_review"} isComplete={statusKind === "approved"} icon={<ExternalLink size={22} />} />
        <ArchitectStep tone="approved" label="Approved" isActive={statusKind === "approved"} isComplete={statusKind === "approved"} icon={<Check size={24} />} />
      </div>
    </div>
  );
}

function ArchitectStep({
  tone,
  label,
  icon,
  isActive,
  isComplete,
}: {
  tone: "running" | "review" | "approved";
  label: string;
  icon: ReactNode;
  isActive: boolean;
  isComplete: boolean;
}) {
  return (
    <div className={`architect-step ${tone} ${isActive ? "active" : ""} ${isComplete ? "complete" : ""}`}>
      <div className="architect-step-circle">
        {icon}
        <strong>{label}</strong>
      </div>
    </div>
  );
}

function formatSessionUpdated(value?: string): string {
  if (!value) return "Unknown";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function resolveSessionSkill(metadata: SkillTaskMetadata | undefined, skills: AgenticSkill[]): AgenticSkill | undefined {
  if (!metadata) return undefined;
  const candidates = [metadata.skillID, metadata.name].filter(Boolean).map((value) => normalizeLookupValue(value ?? ""));
  return skills.find((skill) => {
    const values = [skill.id, skill.name, skill.summary].map(normalizeLookupValue);
    return candidates.some((candidate) => values.includes(candidate));
  });
}

function normalizeLookupValue(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function ProfilePage({ user, skills }: { user: SkillUser; skills: AgenticSkill[] }) {
  const profileSkills = user.skills.length > 0 ? user.skills : skills.filter((skill) => skill.isCreatedByUser);
  const [selectedSkill, setSelectedSkill] = useState<AgenticSkill | null>(null);

  return (
    <section className="profile-layout">
      <h2 className="tab-title">My Profile</h2>
      <div className="surface-panel page-panel">
        <div className="profile-info-list">
          <ProfileInfo label="Email">{user.email}</ProfileInfo>
          <ProfileInfo label="Department">{user.department}</ProfileInfo>
          <ProfileInfo label="Team">{user.team ?? "Unknown"}</ProfileInfo>
        </div>
      </div>
      <div className="profile-skills-section">
        <h2 className="tab-title">My Skills</h2>
        {profileSkills.length > 0 ? (
          <ProfileSkillRows skills={profileSkills} onOpen={setSelectedSkill} />
        ) : (
          <StatePanel title="No skills created" text="Created skills will appear here when they are available." />
        )}
      </div>
      {selectedSkill && <SkillDetailModal skill={selectedSkill} onClose={() => setSelectedSkill(null)} />}
    </section>
  );
}

function ProfileInfo({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="profile-info-item">
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

function StatusBadge({ status }: { status: AgenticSkill["status"] }) {
  return <span className={`badge ${status}`}>{formatStatus(status)}</span>;
}

function TaskBadge({ status, compact = false }: { status: SkillTaskStatus; compact?: boolean }) {
  return <span className={`badge task-${status.kind}`}>{compact ? formatTaskStatus(status).replace(" approval", "") : formatTaskStatus(status)}</span>;
}

function ChatBubble({ message }: { message: SkillChatMessage }) {
  const shouldRenderMarkdown = message.sender === "assistant" && hasMarkdownSyntax(message.text);

  return (
    <div className={message.sender === "user" ? "chat-row user" : "chat-row assistant"}>
      <div className={shouldRenderMarkdown ? "chat-bubble markdown-bubble" : "chat-bubble"}>
        {shouldRenderMarkdown ? <MarkdownContent source={message.text} /> : message.text}
      </div>
    </div>
  );
}

function EmptyChat({ state }: { state: ChatState }) {
  if (state === "create_skill") {
    return <StatePanel title="What do you want to build?" text="Describe the skill or pull request you want Architect to create." />;
  }
  if (state === "brainstorm") {
    return <StatePanel title="What do you want to know?" text="Ask about marketplace skills or explain the task you are trying to complete." />;
  }
  return <StatePanel title="Ask about this skill" text="Use this chat to understand the skill or request a managed change." />;
}

function StatePanel({ icon, title, text }: { icon?: ReactNode; title: string; text: string }) {
  return (
    <div className="state-panel">
      {icon}
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  );
}

function Metadata({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="metadata">
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

function BackButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button className="back-button" type="button" onClick={onClick}>
      <ChevronLeft size={16} aria-hidden="true" />
      {label}
    </button>
  );
}

function toggleSet(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) {
    next.delete(value);
  } else {
    next.add(value);
  }
  return next;
}

function resolveUser(users: SkillUser[], userID?: string): SkillUser {
  return (
    users.find((user) => user.id === userID) ??
    users.find((user) => user.id === currentUser.id || user.email === currentUser.email) ?? {
      id: currentUser.id,
      name: "Andy Gruning",
      email: currentUser.email,
      department: currentUser.department,
      team: currentUser.team,
      skills: [],
    }
  );
}

function routeFromLocation(): Route {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  const params = new URLSearchParams(window.location.search);
  const skillFromQuery = params.get("skill");

  if (skillFromQuery) return { name: "skill", skillID: skillFromQuery };
  if (path === "/") return { name: "suggested" };
  if (path === "/app/suggested") return { name: "suggested" };
  if (path === "/app/skills") return { name: "skills" };
  if (path.startsWith("/app/skills/") && path.endsWith("/chat")) {
    return { name: "skill-chat", skillID: decodeURIComponent(path.replace("/app/skills/", "").replace("/chat", "")) };
  }
  if (path.startsWith("/app/skills/")) return { name: "skill", skillID: decodeURIComponent(path.replace("/app/skills/", "")) };
  if (path === "/app/morpheus") return { name: "morpheus" };
  if (path === "/app/architect") return { name: "architect" };
  if (path.startsWith("/app/profile/")) return { name: "profile", userID: decodeURIComponent(path.replace("/app/profile/", "")) };
  if (path === "/app/profile") return { name: "profile" };
  return { name: "skills" };
}

function pathForRoute(route: Route): string {
  switch (route.name) {
    case "suggested":
      return "/app/suggested";
    case "skills":
      return "/app/skills";
    case "skill":
      return `/app/skills/${encodeURIComponent(route.skillID)}`;
    case "skill-chat":
      return `/app/skills/${encodeURIComponent(route.skillID)}/chat`;
    case "morpheus":
      return "/app/morpheus";
    case "architect":
      return "/app/architect";
    case "profile":
      return route.userID ? `/app/profile/${encodeURIComponent(route.userID)}` : "/app/profile";
  }
}

function placeholderForState(state: ChatState): string {
  if (state === "create_skill") return "Describe the skill or PR you want to create";
  if (state === "skill_chat") return "Ask about this skill or request a change";
  return "Ask about skills or what you can do with them";
}

function newLocalSession(state: ChatState): SkillChatSession {
  const id = `${state.replace("_", "-")}-session-${crypto.randomUUID().slice(0, 8)}`;
  const title = state === "brainstorm" ? "New chat" : state === "create_skill" ? "New skill request" : "New session";
  return { id, title, messages: [], updatedAt: new Date().toISOString() };
}

function upsertSession(snapshot: SessionSnapshot, session: SkillChatSession, status?: SkillTaskStatus, oldID?: string): SessionSnapshot {
  const sessions = [session, ...snapshot.sessions.filter((candidate) => candidate.id !== session.id && candidate.id !== oldID)];
  const taskStatuses = { ...snapshot.taskStatuses };
  if (oldID) delete taskStatuses[oldID];
  if (status) taskStatuses[session.id] = status;
  return { ...snapshot, sessions, taskStatuses, hasLoaded: true };
}

function mergeSnapshot(current: SessionSnapshot, next: SessionSnapshot): SessionSnapshot {
  return {
    ...current,
    sessions: [...next.sessions, ...current.sessions.filter((session) => !next.sessions.some((candidate) => candidate.id === session.id))],
    taskStatuses: { ...current.taskStatuses, ...next.taskStatuses },
    taskMetadata: { ...current.taskMetadata, ...next.taskMetadata },
    hasLoaded: true,
  };
}
